import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchAccounts } from '@accountHooks';
import { useRentPayments } from '@/hooks/useRentPayments';

export const rentPaymentSchema = z.object({
  amount: z.coerce
    .number({ invalid_type_error: 'Amount must be a number' })
    .positive('Amount must be greater than 0'),
  payment_date: z.string().min(1, 'Payment date is required'),
  account_id: z.string().min(1, 'Account is required'),
  description: z.string().optional(),
});

export type RentPaymentFormData = z.infer<typeof rentPaymentSchema>;

interface RentPaymentFormProps {
  apartmentId: string;
  expectedRent: number;
  rentDayOfMonth: number;
  onSuccess?: () => void;
}

export function computeNextRentDate(rentDayOfMonth: number): string {
  const today = new Date();
  const day = today.getDate();
  let year = today.getFullYear();
  let month = today.getMonth();

  if (day <= rentDayOfMonth) {
    // Use this month
  } else {
    // Use next month
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  const date = new Date(year, month, rentDayOfMonth);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function RentPaymentForm({
  apartmentId,
  expectedRent,
  rentDayOfMonth,
  onSuccess,
}: RentPaymentFormProps) {
  const { data: accounts = [], isLoading: accountsLoading } = useFetchAccounts();
  const { createRentPayment } = useRentPayments(apartmentId);

  const form = useForm<RentPaymentFormData>({
    resolver: zodResolver(rentPaymentSchema),
    defaultValues: {
      amount: expectedRent,
      payment_date: computeNextRentDate(rentDayOfMonth),
      account_id: '',
      description: '',
    },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (data: RentPaymentFormData) => {
    try {
      await createRentPayment.mutateAsync({
        apartment_id: apartmentId,
        account_id: data.account_id,
        amount: data.amount,
        payment_date: data.payment_date,
        description: data.description || undefined,
      });
      form.reset({
        amount: expectedRent,
        payment_date: computeNextRentDate(rentDayOfMonth),
        account_id: '',
        description: '',
      });
      onSuccess?.();
    } catch {
      toast.error('Failed to record rent payment. Please try again.');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount *</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="payment_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment Date *</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="account_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Account *</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={accountsLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an account" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.nickname || account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Add a description..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={isSubmitting || createRentPayment.isPending}>
            {isSubmitting || createRentPayment.isPending ? 'Recording...' : 'Record Payment'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
